/**
 * navConfig -- Navigation "Performance OS" (RBAC + Sport-aware)
 *
 * Consumed by:
 * - GlobalSidebar (desktop)
 * - MobileMenuButton (mobile)
 */

import { FEATURES } from './features';
import { getCapabilities } from './sportCapabilities';

const normalizeRoleKey = (roleKey) => {
  if (!roleKey) return null;
  return String(roleKey).toUpperCase();
};

const getDashboardRouteForRole = (roleKey) => {
  switch (normalizeRoleKey(roleKey)) {
    case 'TREINADOR':
      return '/coach';
    case 'GESTOR':
      return '/manager';
    case 'RESEARCH':
      return '/research';
    case 'ADMIN':
      return '/governance';
    case 'ATLETA':
    default:
      return '/athlete';
  }
};

export const getNavSections = ({
  roleKey,
  sportKey,
  isAdmin = false,
  features = FEATURES,
} = {}) => {
  const dashboardRoute = getDashboardRouteForRole(roleKey);
  const caps = getCapabilities(sportKey);

  return [
    {
      id: 'core',
      title: 'Performance OS',
      items: [
        { to: dashboardRoute, label: 'Dashboard', iconKey: 'home', match: 'startsWith' },
        { to: '/central', label: 'Central', iconKey: 'activity', match: 'exact', requiredRoles: ['ATLETA', 'TREINADOR', 'PREPARADOR_FISICO'], badge: 'NEW' },
        { to: '/meus-campeonatos', label: 'Meus Campeonatos', iconKey: 'competitions', match: 'exact', requiredRoles: ['ATLETA', 'TREINADOR', 'GESTOR'] },
        { to: '/agenda', label: 'Agenda', iconKey: 'events', match: 'startsWith' },
        ...(features?.ENABLE_SOCIAL_FEED ? [{ to: '/feed', label: 'Social Feed', iconKey: 'users', match: 'exact', badge: 'BETA' }] : []),
      ],
    },
    {
      id: 'athlete',
      title: 'Minha Area',
      requiredRoles: ['ATLETA'],
      items: [
        ...(features?.ENABLE_TRAINING ? [
          { to: `/my/training/${sportKey || 'swimming'}/today`, label: 'Treino Hoje', iconKey: 'activity', match: 'startsWith', badge: 'NEW' },
          { to: `/my/training/${sportKey || 'swimming'}/sessions`, label: 'Sessoes', iconKey: 'events', match: 'startsWith' },
          { to: `/my/training/${sportKey || 'swimming'}/insights`, label: 'Insights', iconKey: 'insights', match: 'startsWith' },
        ] : []),
        { to: '/my/wellness', label: 'Bem-Estar', iconKey: 'heart', match: 'startsWith' },
      ],
    },
    {
      id: 'coach',
      title: 'Area do Treinador',
      requiredRoles: ['TREINADOR'],
      items: [
        { to: '/coach', label: 'Meus Atletas', iconKey: 'people', match: 'exact', badge: 'NEW' },
        { to: '/treinos', label: 'Treinos', iconKey: 'activity', match: 'startsWith' },
      ],
    },
    {
      id: 'intelligence',
      title: 'Inteligencia',
      items: [
        { to: '/analytics', label: 'Analytics', iconKey: 'analytics', match: 'startsWith' },
        { to: '/mind', label: 'Mind', iconKey: 'mind', match: 'startsWith', badge: 'AI' },
        { to: '/library', label: 'Biblioteca Arena', iconKey: 'fileText', match: 'startsWith', requiredRoles: ['TREINADOR', 'GESTOR'] },
        { to: '/research', label: 'Pesquisa', iconKey: 'search', match: 'startsWith', badge: 'AI' },
      ],
    },
    {
      id: 'strategy',
      title: 'Estrategico',
      items: [
        ...(caps.tacticsEnabled ? [{ to: '/tactics/designer', label: 'Tatico', iconKey: 'football', match: 'startsWith', requiredRoles: ['TREINADOR', 'GESTOR'] }] : []),
        ...(caps.playbookEnabled ? [{ to: '/playbook/designer', label: 'Playbook', iconKey: 'football', match: 'startsWith', requiredRoles: ['TREINADOR'], isNew: true }] : []),
      ],
    },
    {
      id: 'manager',
      title: 'Gestao do Clube',
      requiredRoles: ['GESTOR'],
      items: [
        { to: '/manager', label: 'Dashboard', iconKey: 'analytics', match: 'exact', badge: 'NEW' },
        { to: '/manager/members', label: 'Membros', iconKey: 'users', match: 'startsWith' },
        { to: '/manager/approvals', label: 'Aprovacoes', iconKey: 'competitions', match: 'startsWith' },
      ],
    },
    // recruiter section — Fase 2
    {
      id: 'governance',
      title: 'Gestao',
      adminOnly: true,
      collapsible: true,
      iconKey: 'settings',
      items: [
        { to: '/governance', label: 'Overview', iconKey: 'activity', match: 'exact', badge: 'FOUNDER' },
        { to: '/governance/exec/planner', label: 'Planner', iconKey: 'activity', match: 'startsWith' },
        { to: '/governance/exec/builder', label: 'Builder', iconKey: 'activity', match: 'startsWith' },
        { to: '/governance/content', label: 'Conteudo', iconKey: 'creator', match: 'startsWith' },
        { to: '/governance/ti/agentes', label: 'Agentes AI', iconKey: 'mind', match: 'startsWith' },
        { to: '/governance/ti/control-tower', label: 'Control Tower', iconKey: 'analytics', match: 'startsWith' },
        { to: '/governance/ti/parser-monitor', label: 'Parser Monitor', iconKey: 'insights', match: 'startsWith' },
        { to: '/governance/fin/custos', label: 'Custos', iconKey: 'insights', match: 'startsWith' },
      ],
    },
    {
      id: 'directory',
      title: 'Diretorio',
      items: [
        { to: '/jogadores', label: 'Pessoas', iconKey: 'people', match: 'startsWith' },
        { to: '/clubes', label: 'Organizacoes', iconKey: 'organizations', match: 'startsWith' },
        { to: '/locais', label: 'Locais', iconKey: 'venues', match: 'startsWith' },
      ],
    },
  ];
};

export const filterNavSections = (sections, { roleKey, isAdmin = false, superMode = false, hasCapability = () => false } = {}) => {
  const normalizedRole = normalizeRoleKey(roleKey);

  return (sections || [])
    .map((section) => {
      // adminOnly sections (governance) require super mode
      if (section.adminOnly && !(isAdmin && superMode)) return { ...section, items: [] };
      // Section-level role requirement
      if (section.requiredRoles?.length) {
        const roleAllowed = normalizedRole && section.requiredRoles.includes(normalizedRole);
        if (!roleAllowed && !(isAdmin && superMode)) return { ...section, items: [] };
      }

      const visibleItems = (section.items || []).filter((item) => {
        // Super mode: all items visible
        if (isAdmin && superMode) return true;
        // Capability + role: either is sufficient
        if (item.capability && item.requiredRoles?.length) {
          return hasCapability(item.capability) || (normalizedRole && item.requiredRoles.includes(normalizedRole));
        }
        // Capability only: strict check
        if (item.capability) {
          return hasCapability(item.capability);
        }
        // Role only
        if (item.requiredRoles?.length) {
          return normalizedRole && item.requiredRoles.includes(normalizedRole);
        }
        // Admin only item
        if (item.adminOnly) return isAdmin;
        return true;
      });

      return { ...section, items: visibleItems };
    })
    .filter((section) => (section.items || []).length > 0);
};

export const getVisibleNavSections = ({ roleKey, sportKey, isAdmin = false, features, superMode = false, hasCapability } = {}) => {
  const sections = getNavSections({ roleKey, sportKey, isAdmin, features });
  return filterNavSections(sections, { roleKey, isAdmin, superMode, hasCapability });
};
